import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AnalyticsEvent, captureAnalyticsEvent } from '../../constant/Events';
import { useAuth } from '../../hooks';
import { useCompany } from '../../hooks/useCompany';
import { ColorType, Label, LabelType } from '../_ui';
import { CreateCompanySelfServeForm } from '../company/CreateCompanySelfServeForm';

// There is currently NO VALIDATION on Hubspot contact ID values.
// The field is currently only used as a flag to indicate that the user has come from the onboarding URL
// in the email invitation. It is entirely possible for a user to craft their own URL with a valid
// Hubspot contact ID and gain access to this page/set that value on their profile.
// This is fine for now, but do not use this value to grant access to any features of the app.

const CreateCompanySelfServe = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const [searchParams] = useSearchParams();

  const { companies } = useCompany();

  const { hsContactId } = auth.user.attributes.profile;
  const hsContactIdQueryParam = searchParams.get('hsContactId');

  // Track company creation page arrival with hsContactId parameter
  // Must be called before any early returns to comply with React hooks rules
  useEffect(() => {
    const contactId = hsContactId || hsContactIdQueryParam;
    if (contactId && auth.user) {
      captureAnalyticsEvent(AnalyticsEvent.SELF_SERVE_COMPANY_CREATION_PAGE_ARRIVED, {
        hubSpotContactId: contactId || undefined,
        userEmail: auth.user?.attributes?.email || null,
      });
    }
  }, [hsContactId, hsContactIdQueryParam, auth.user?.attributes?.email, auth.user]);

  if (
    !auth.user ||
    // If the user already has access to a company, redirect to the dashboard
    (companies?.length ?? 0) > 0 ||
    // If the user has not come from the onboarding URL, either previously or on this attempt,
    // redirect to the dashboard
    (!hsContactId && !hsContactIdQueryParam)
  ) {
    return <Navigate to='/' replace />;
  }

  if (!hsContactId && hsContactIdQueryParam) {
    auth.updateHubspotContactId(hsContactIdQueryParam);
  }

  return (
    <div className='flex flex-col items-center'>
      <div className='w-[600px] bg-white rounded py-[48px] px-[40px] border-grey0 items-center'>
        <div className='pb-[18px]'>
          <Label type={LabelType.H3} color={ColorType.BLACK} text={t('create_company')} />
          <Label
            type={LabelType.BODY2}
            color={ColorType.GREY5}
            text={t('create_company_description')}
            className='pt-2'
          />
        </div>
        <div>
          <CreateCompanySelfServeForm />
        </div>
      </div>
    </div>
  );
};

export default CreateCompanySelfServe;
